import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPANS = [
  { k: "15m", l: "15m" },
  { k: "30m", l: "30m" },
  { k: "1h", l: "1h" },
  { k: "2h", l: "2h" },
  { k: "4h", l: "4h" },
  { k: "1d", l: "1d" },
];

const BUY_COLORS = ["#26A69A", "#4CB6AB", "#7ECBC2"];
const SELL_COLORS = ["#EF5350", "#F27C7A", "#F4A6A4"];

function Donut({ data }) {
  const size = 220;
  const cx = size / 2;
  const cy = size / 2;
  const r = 80;
  const iR = 55;
  const parts = [
    ...data.sell.map((v, i) => ({ v, color: SELL_COLORS[i], side: "sell", i })),
    ...data.buy.map((v, i) => ({ v, color: BUY_COLORS[i], side: "buy", i })),
  ];
  const total = parts.reduce((s, p) => s + p.v, 0) || 1;
  let a = -Math.PI / 2;
  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="h-56 w-56">
      {parts.map((p, k) => {
        const span = (p.v / total) * Math.PI * 2;
        const a2 = a + span;
        const large = span > Math.PI ? 1 : 0;
        const x1 = cx + r * Math.cos(a);
        const y1 = cy + r * Math.sin(a);
        const x2 = cx + r * Math.cos(a2);
        const y2 = cy + r * Math.sin(a2);
        const ix1 = cx + iR * Math.cos(a2);
        const iy1 = cy + iR * Math.sin(a2);
        const ix2 = cx + iR * Math.cos(a);
        const iy2 = cy + iR * Math.sin(a);
        const d = [
          `M ${x1} ${y1}`,
          `A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`,
          `L ${ix1} ${iy1}`,
          `A ${iR} ${iR} 0 ${large} 0 ${ix2} ${iy2}`,
          "Z",
        ].join(" ");
        a = a2;
        return <path key={k} d={d} fill={p.color} />;
      })}
    </svg>
  );
}

function Bars({ bars, symbol }) {
  const w = 400;
  const h = 140;
  const pad = 24;
  const max = Math.max(...bars.map((b) => Math.abs(b.value)), 1);
  const midY = h - pad;
  const usableH = h - pad * 2;
  const bw = (w - pad * 2) / bars.length - 12;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="h-36 w-full">
      <line x1={pad} y1={midY} x2={w - pad} y2={midY} stroke="rgba(255,255,255,0.1)" />
      {bars.map((b, i) => {
        const bh = (Math.abs(b.value) / max) * usableH;
        const x = pad + i * ((w - pad * 2) / bars.length);
        const isLast = i === bars.length - 1;
        const isPositive = b.value >= 0;
        return (
          <g key={i}>
            <rect
              x={x}
              y={isPositive ? midY - bh : midY}
              width={bw}
              height={bh}
              fill={isPositive ? "#26A69A" : "#EF5350"}
              opacity={isLast ? 0.55 : 1}
            />
            <text
              x={x + bw / 2}
              y={isPositive ? midY - bh - 6 : midY + bh + 12}
              textAnchor="middle"
              fill={isPositive ? "#26A69A" : "#EF5350"}
              fontSize="10"
              fontFamily="monospace"
            >
              {b.value.toFixed(2)}
            </text>
          </g>
        );
      })}
      <text x={w - pad} y={12} textAnchor="end" fontSize="9" fill="rgba(255,255,255,0.4)">
        Large inflow · {symbol}
      </text>
    </svg>
  );
}

export default function MoneyFlow({ symbol }) {
  const [span, setSpan] = useState("1d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!symbol) return undefined;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/markets/moneyflow/${symbol}?span=${span}`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [symbol, span]);

  const fmt = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 4, maximumFractionDigits: 4 });

  return (
    <div data-testid="moneyflow-panel" className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-[0.3em] text-white/50">Money flow analysis</p>
        <div className="flex gap-1">
          {SPANS.map((s) => (
            <button
              key={s.k}
              data-testid={`mf-span-${s.k}`}
              onClick={() => setSpan(s.k)}
              className={`rounded px-2 py-1 text-[10px] tracking-widest transition-colors ${
                span === s.k ? "bg-white/10 text-white" : "text-white/40 hover:text-white/70"
              }`}
            >
              {s.l}
            </button>
          ))}
        </div>
      </div>

      <div className="relative flex justify-center">
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-gold" />
          </div>
        )}
        {data && (
          <div className="flex flex-col items-center gap-4">
            <Donut data={data.donut} />
            <div className="flex flex-wrap justify-center gap-3 text-[10px]">
              {data.donut.buy.map((v, i) => (
                <span key={`b${i}`} className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-sm" style={{ background: BUY_COLORS[i] }} />
                  <span className="text-[#26A69A] font-tabular">+{v.toFixed(2)}%</span>
                </span>
              ))}
              {data.donut.sell.map((v, i) => (
                <span key={`s${i}`} className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-sm" style={{ background: SELL_COLORS[i] }} />
                  <span className="text-[#EF5350] font-tabular">−{v.toFixed(2)}%</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {data && (
        <div className="border border-white/10 bg-ink/40">
          <div className="grid grid-cols-4 gap-2 border-b border-white/10 px-4 py-2 text-[10px] uppercase tracking-widest text-white/40">
            <span>Orders</span>
            <span className="text-right">Buy ({symbol})</span>
            <span className="text-right">Sell ({symbol})</span>
            <span className="text-right">Inflow</span>
          </div>
          {data.orders.map((r) => (
            <div key={r.size} className="grid grid-cols-4 gap-2 border-b border-white/5 px-4 py-2 text-xs last:border-b-0">
              <span className="text-white/70">{r.size}</span>
              <span className="text-right font-tabular text-[#26A69A]">{fmt(r.buy)}</span>
              <span className="text-right font-tabular text-[#EF5350]">{fmt(r.sell)}</span>
              <span className={`text-right font-tabular ${r.inflow >= 0 ? "text-white/80" : "text-white/70"}`}>{fmt(r.inflow)}</span>
            </div>
          ))}
          <div className="grid grid-cols-4 gap-2 px-4 py-2 text-xs">
            <span className="text-white/50">Total</span>
            <span className="text-right font-tabular text-white/85">{fmt(data.total.buy)}</span>
            <span className="text-right font-tabular text-white/85">{fmt(data.total.sell)}</span>
            <span className={`text-right font-tabular ${data.total.inflow >= 0 ? "text-[#26A69A]" : "text-[#EF5350]"}`}>
              {fmt(data.total.inflow)}
            </span>
          </div>
        </div>
      )}

      {data && (
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/50">5 × 24 hours large inflow ({symbol})</p>
          <Bars bars={data.bars} symbol={symbol} />
        </div>
      )}
    </div>
  );
}
